import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";

import { SRC_LISTINGS, type MapHandle } from "@/map/MapCanvas.tsx";
import { useSwipe } from "@/platform/use-swipe.ts";

/** Half-width of the pin's screen footprint plus a little air, in px. */
const SUPPRESS_PX = 26;

/**
 * A single hand-placed marker, unrelated to the listings feed.
 *
 * It deliberately does NOT go through the listings source: it is not a rental,
 * it must not be filtered out by freshness or price, and it must never be folded
 * into a cluster. A DOM marker keeps it entirely outside the map's source/layer
 * sync, so nothing here can perturb the listing pipeline.
 */

const PIN = {
  label: "539 Bryant St",
  title: "Reactor Inc.",
  lngLat: [-122.3967867, 37.7803268] as [number, number],
  // Mirrored to our own R2 rather than hotlinked: the origin CDN answers 403 to
  // any foreign referer, so the video and its poster both failed silently and the
  // card came up empty. <video> has no referrerPolicy escape hatch the way <img>
  // does, so serving the bytes ourselves is the only fix. R2 egress is free.
  //
  // Re-encoded from 1920x1080 to 640 wide, which is still 2x this card: 3.6 MB
  // to 463 KB. Only h264 is offered — at this size VP9 came out nearly twice as
  // large, so a webm listed first would have made browsers pick the worse file.
  poster: "https://assets.toddelvers.com/reactor/logo.jpg",
  sources: [{ src: "https://assets.toddelvers.com/reactor/logo.mp4", type: "video/mp4" }],
};

export function ReactorPin({
  handle,
  ready,
  listingSelected,
  onOpen,
}: {
  handle: React.RefObject<MapHandle | null>;
  /** Re-render trigger: the map instance lives behind a ref. */
  ready: boolean;
  /** A listing is showing. Only one sheet may own the bottom of the screen. */
  listingSelected: boolean;
  /** Ask the app to drop its listing, so this can take the sheet over. */
  onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);

  // The marker's click handler is installed once, so it reads the live callback
  // through a ref rather than capturing the first render's copy.
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  // The two sheets are mutually exclusive: selecting a listing dismisses this,
  // and opening this dismisses the listing (see the marker handler). Without
  // both directions they stack on top of each other at the bottom of the screen.
  useEffect(() => {
    if (listingSelected) setOpen(false);
  }, [listingSelected]);

  // Identical recogniser to the listing sheet's, so the gesture feels the same
  // and the same Android pull-to-refresh fix applies to both.
  const swipe = useSwipe({
    axis: "y",
    allow: ["down"],
    track: true,
    threshold: 72,
    onSwipe: () => setOpen(false),
  });
  const markerRef = useRef<maplibregl.Marker | null>(null);
  /** Cluster ids currently hidden because they sit under the pin. */
  const suppressedRef = useRef<Set<string | number>>(new Set());

  useEffect(() => {
    const map = handle.current?.map;
    if (!map || markerRef.current) return;

    const el = document.createElement("button");
    el.type = "button";
    el.setAttribute("aria-label", `${PIN.title}, ${PIN.label}`);
    // Styled here rather than in app.css: the marker element is created
    // imperatively by MapLibre's Marker, so it never passes through Tailwind's
    // class scanner and a utility class would be purged from the build.
    //
    // A bare red dot. The ring that used to sit around it was separating the pin
    // from cluster bubbles drawn behind it; that job now belongs entirely to the
    // screen-space suppression below, which removes any overlapping bubble
    // outright rather than hiding the overlap. The drop shadow stays — it is what
    // keeps the dot legible over a pale basemap in light mode.
    Object.assign(el.style, {
      width: "16px",
      height: "16px",
      borderRadius: "9999px",
      background: "#ef4444",
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.45)",
      cursor: "pointer",
      padding: "0",
      border: "0",
      zIndex: "5",
    } satisfies Partial<CSSStyleDeclaration>);
    el.addEventListener("click", (e) => {
      // Otherwise the map's own click handler treats this as a click on bare
      // map and clears whatever is selected.
      e.stopPropagation();
      setOpen((v) => {
        if (!v) onOpenRef.current();
        return !v;
      });
    });

    markerRef.current = new maplibregl.Marker({ element: el }).setLngLat(PIN.lngLat).addTo(map);

    // Give the pin right of way over aggregates. Circles take no part in
    // MapLibre's collision system, so no style rule can make a cluster bubble
    // move aside — the only way they can never overlap is to hit-test in screen
    // space and take the offending bubble out. The listing itself is not lost:
    // the aggregate is only hidden while it is standing on the pin, and zooming
    // in breaks it apart as usual.
    const suppress = () => {
      const p = map.project(PIN.lngLat);
      const box: [[number, number], [number, number]] = [
        [p.x - SUPPRESS_PX, p.y - SUPPRESS_PX],
        [p.x + SUPPRESS_PX, p.y + SUPPRESS_PX],
      ];
      let hit: Array<string | number> = [];
      try {
        hit = map
          .queryRenderedFeatures(box, { layers: ["clusters"] })
          .map((f) => f.id)
          .filter((id): id is string | number => id !== undefined);
      } catch {
        // The layer is absent between a style swap and its re-install.
        return;
      }
      const next = new Set(hit);
      for (const id of suppressedRef.current) {
        if (!next.has(id)) map.removeFeatureState({ source: SRC_LISTINGS, id }, "suppressed");
      }
      for (const id of next) {
        map.setFeatureState({ source: SRC_LISTINGS, id }, { suppressed: true });
      }
      suppressedRef.current = next;
    };

    // `move` covers the drag itself, `idle` catches the re-cluster that lands
    // after the camera has already stopped.
    map.on("move", suppress);
    map.on("idle", suppress);
    suppress();

    return () => {
      map.off("move", suppress);
      map.off("idle", suppress);
      markerRef.current?.remove();
      markerRef.current = null;
    };
  }, [handle, ready]);

  if (!open) return null;

  return (
    // Bottom-LEFT on desktop and lifted clear of the dock on mobile: the
    // bottom-right corner belongs to the zoom/locate stack, and this was landing
    // on top of it.
    // The listing sheet's shell, verbatim: same wrapper, same touch-action, same
    // follow-the-finger transform, same handle. Only the contents are shorter.
    <div
      {...swipe.handlers}
      className="fixed inset-x-0 bottom-0 z-40 animate-in duration-200 slide-in-from-bottom-4"
      style={{
        touchAction: "none",
        paddingBottom: "env(safe-area-inset-bottom)",
        transform: `translateY(${Math.max(0, swipe.offset)}px)`,
        transition: swipe.dragging ? "none" : "transform 180ms ease-out",
      }}
    >
      <div className="console console-grid w-full rounded-t-2xl text-chalk">
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="flex w-full cursor-grab touch-none items-center justify-center px-4 py-2.5 active:cursor-grabbing"
        >
          <span className="h-1.5 w-11 rounded-full bg-chalk-mute/70" />
        </button>

        {/* A tenth taller than the video itself (10rem -> 11rem), with the extra
            rem sitting above it. The fill is sampled from the animation's own
            top-left pixel rather than assumed: it is #fefefe, and pure white
            would leave a visible seam across the join. */}
        <div className="relative h-44 w-full overflow-hidden" style={{ background: "#fefefe" }}>
          <video
            // object-contain rather than cover, plus padding: the frame keeps its
            // size and the logo shrinks inside it. This only reads as breathing
            // room because the container is filled with the animation's own
            // #fefefe — against any other colour the inset would show as a box.
            className="absolute inset-x-0 bottom-0 h-40 w-full object-contain p-5"
            poster={PIN.poster}
            autoPlay
            loop
            muted
            playsInline
          >
            {PIN.sources.map((s) => (
              <source key={s.src} src={s.src} type={s.type} />
            ))}
          </video>
        </div>

        {/* Shorter than a listing by omission: no beds/baths, no source chips,
            no call to action, because there is nothing here to go and view. */}
        <div className="p-3.5">
          <span className="block font-mono text-[22px] leading-none font-medium tracking-tight">
            {PIN.title}
          </span>
          <p className="pt-2 text-[13px] leading-snug font-medium text-chalk">{PIN.label}</p>
        </div>
      </div>
    </div>
  );
}
